import classNames from 'classnames';
import React, { useState } from 'react';
import { useAutoSave } from 'components/styleguide/hooks/useAutoSave';

const styles = require('./Radio.module.scss');

export interface Props {
  autoSaveUrl?: string;
  checked?: boolean;
  className?: string;
  context?: 'talent' | 'company' | 'neutral';
  disabled?: boolean;
  id?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  label?: string | JSX.Element;
  name?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, name?: string, value?: string) => void;
  required?: boolean;
  value: string;
}

const Radio: React.FC<Props> = ({
  autoSaveUrl,
  checked,
  className,
  context = 'neutral',
  disabled,
  id,
  inputRef,
  label,
  name,
  onChange,
  required,
  value,
}) => {
  const [resolvedId] = useState(() => id || `radio-${Math.random().toString(36).slice(2, 9)}`);
  const { save } = useAutoSave({ url: autoSaveUrl || '', name: name || '', debounceMs: 0 });
  const isControlled = onChange !== undefined;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (autoSaveUrl) save(value);
    if (onChange) onChange(event, name, value);
  };

  const wrapperClass = classNames(
    styles['ui-radio'],
    styles[`ui-radio--${context}`],
    className,
  );

  const circleClass = classNames(
    styles['ui-radio__circle'],
    {
      [styles['ui-radio__circle--disabled']]: disabled,
    },
  );

  return (
    <div className={wrapperClass}>
      <label className={styles['ui-radio__container']}>
        <input
          checked={isControlled ? checked : undefined}
          className={styles['ui-radio__input']}
          defaultChecked={!isControlled ? checked : undefined}
          disabled={disabled}
          id={resolvedId}
          name={name}
          onChange={handleChange}
          ref={inputRef}
          required={required}
          type="radio"
          value={value}
        />
        <span className={styles['ui-radio__circle-halo']} aria-hidden="true">
          <span className={circleClass}>
            <span className={styles['ui-radio__dot']} />
          </span>
        </span>
        {label && (
          <span className={styles['ui-radio__label']}>
            {typeof label === 'string'
              ? <span dangerouslySetInnerHTML={{ __html: label }} />
              : label}
          </span>
        )}
      </label>
    </div>
  );
};

export default Radio;
